import {
  createDinero,
  Currency,
} from '@mr-yum/frontend-core/dist/services/createDinero'

import {
  BasicInvoicePartsFragment,
  LineItemKind,
  LineItemPartsFragment,
  LineItemType,
} from '@/gql/graphql'

import { GroupedLineItemsProps } from './types'

export const priceExcludeTax = (
  priceIncludeTax: number,
  currency: Currency,
  taxRate: number,
): number => {
  return createDinero(priceIncludeTax, currency)
    .divide(1 + taxRate / 100)
    .getAmount()
}

export const getTax = (
  priceIncludeTax: number,
  currency: Currency,
  taxRate: number,
): number => {
  const excludeTax = createDinero(
    priceExcludeTax(priceIncludeTax, currency, taxRate),
    currency,
  )
  return createDinero(priceIncludeTax, currency)
    .subtract(excludeTax)
    .getAmount()
}

export const getTotalSurcharge = (items: LineItemPartsFragment[]) => {
  let totalSurchargeInCents = 0

  for (const item of items) {
    // item.priceInCents is the post-surcount unit price in cents; for Reversal item, item.priceInCents is negative
    // item.originalPriceInCents is the pre-surcount unit price in cents; for Reversal item, item.originalPriceInCents is also positive
    totalSurchargeInCents +=
      (item.priceInCents -
        item.originalPriceInCents *
          (item.kind === LineItemKind.Affirmation ? 1 : -1)) *
      item.quantity
  }

  return totalSurchargeInCents
}

export const groupItemsByVenue = (
  items: LineItemPartsFragment[],
  groupTitle?: string,
): GroupedLineItemsProps[] => {
  const splitItems = splitLineItems(items)

  return [
    {
      groupTitle,
      ...splitItems,
    },
  ]
}

export const groupItemsByVenues = (
  orders: BasicInvoicePartsFragment[],
): GroupedLineItemsProps[] => {
  const groupedItems: GroupedLineItemsProps[] = []

  for (const order of orders) {
    const splitItems = splitLineItems(order.lineItems)

    groupedItems.push({
      groupTitle: order.venue.name,
      groupSlug: order.venue.slug,
      orderId: order.id,
      menuItems: splitItems.menuItems,
      additionalItems: splitItems.additionalItems,
    })
  }

  return groupedItems
}

export const splitLineItems = (items: LineItemPartsFragment[]) => {
  const additionalItems = []
  const menuItems = []

  for (const item of items) {
    switch (item.type) {
      case LineItemType.Gratuity:
      case LineItemType.AbsoluteDiscount:
      case LineItemType.CardFee:
      case LineItemType.DeliveryFee:
      case LineItemType.ServiceCharge:
        additionalItems.push(item)
        break
      default:
        menuItems.push(item)
    }
  }

  return {
    menuItems,
    additionalItems,
  }
}

export const flattenAdditionalItems = (groups: GroupedLineItemsProps[]) => {
  const additionalItems = []

  let gratuityItem: LineItemPartsFragment | undefined = undefined
  let cardFeeItem: LineItemPartsFragment | undefined = undefined
  let serviceChargeItem: LineItemPartsFragment | undefined = undefined

  for (const group of groups) {
    for (const item of group.additionalItems) {
      switch (item.type) {
        case LineItemType.Gratuity: {
          let gPriceInCents: number = gratuityItem?.priceInCents ?? 0
          gPriceInCents += item.priceInCents

          gratuityItem = {
            ...item,
            priceInCents: gPriceInCents,
          }
          break
        }
        case LineItemType.CardFee: {
          let cPriceInCents: number = cardFeeItem?.priceInCents ?? 0
          cPriceInCents += item.priceInCents

          cardFeeItem = {
            ...item,
            priceInCents: cPriceInCents,
          }
          break
        }
        case LineItemType.ServiceCharge: {
          let sPriceInCents: number = serviceChargeItem?.priceInCents ?? 0
          sPriceInCents += item.priceInCents

          serviceChargeItem = {
            ...item,
            priceInCents: sPriceInCents,
          }
          break
        }
        default:
          additionalItems.push(item)
          break
      }
    }
  }

  if (cardFeeItem) {
    additionalItems.push(cardFeeItem)
  }

  if (gratuityItem) {
    additionalItems.push(gratuityItem)
  }

  if (serviceChargeItem) {
    additionalItems.push(serviceChargeItem)
  }

  return additionalItems
}
